import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export type StorefrontVariant = {
  id: number;
  title: string;
  available: boolean;
  price: number;
  compareAtPrice: number | null;
  sellingPlanId: number | null;
};

export type StorefrontProduct = {
  id: number;
  handle: string;
  title: string;
  firstVariant: StorefrontVariant | null;
};

type StorefrontMoney = {
  amount: string;
};

type StorefrontProductResponse = {
  data?: {
    product?: {
      id: string;
      handle: string;
      title: string;
      variants: {
        nodes: Array<{
          id: string;
          title: string;
          availableForSale: boolean;
          price: StorefrontMoney;
          compareAtPrice: StorefrontMoney | null;
        }>;
      };
    } | null;
  };
};

const SHOPIFY_DOMAIN = "halm-club.myshopify.com";
const STOREFRONT_TOKEN = "2af74335d38288a0dfa378f89c76f8c1";

const fromGid = (gid: string) => Number(gid.split("/").pop());

const toCents = (money: StorefrontMoney | null) =>
  money ? Math.round(Number(money.amount) * 100) : null;

export const shopifyStorefrontApi = createApi({
  reducerPath: "shopifyStorefrontApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `https://${SHOPIFY_DOMAIN}/api/2025-01/graphql.json`,
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      headers.set("X-Shopify-Storefront-Access-Token", STOREFRONT_TOKEN);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getProductFirstVariant: builder.query<StorefrontProduct | null, string>({
      query: (handle) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query ProductFirstVariant($handle: String!) {
              product(handle: $handle) {
                id
                handle
                title
                variants(first: 1) {
                  nodes {
                    id
                    title
                    availableForSale
                    price {
                      amount
                    }
                    compareAtPrice {
                      amount
                    }
                  }
                }
              }
            }
          `,
          variables: {
            handle,
          },
        },
      }),
      transformResponse: (response: StorefrontProductResponse) => {
        const product = response.data?.product;
        const variant = product?.variants.nodes[0];

        if (!product || !variant) {
          return null;
        }

        return {
          id: fromGid(product.id),
          handle: product.handle,
          title: product.title,
          firstVariant: {
            id: fromGid(variant.id),
            title: variant.title,
            available: variant.availableForSale,
            price: toCents(variant.price) ?? 0,
            compareAtPrice: toCents(variant.compareAtPrice),
            sellingPlanId: null,
          },
        };
      },
    }),
  }),
});

export const { useGetProductFirstVariantQuery } = shopifyStorefrontApi;
